import { computed, reactive } from 'vue'
import { apiFetch } from './api'

// Preferência de fuso horário do usuário — afeta apenas a EXIBIÇÃO de horários.
// mode 'auto': usa o fuso detectado (IP com fallback para o fuso do navegador).
// mode 'manual': usa o fuso escolhido na lista.

const LS_KEY = 'ot_user_timezone'

interface TimezoneState {
  mode: 'auto' | 'manual'
  manualTz: string
  ipTz: string
  ipChecked: boolean
  serverLoaded: boolean
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

export function isValidTimeZone(tz: string): boolean {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

function loadLocal(): Pick<TimezoneState, 'mode' | 'manualTz'> {
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && (parsed.mode === 'manual' || parsed.mode === 'auto')) {
        return { mode: parsed.mode, manualTz: isValidTimeZone(parsed.manualTz) ? parsed.manualTz : '' }
      }
    }
  } catch { /* ignore */ }
  return { mode: 'auto', manualTz: '' }
}

const state = reactive<TimezoneState>({
  ...loadLocal(),
  ipTz: '',
  ipChecked: false,
  serverLoaded: false
})

function persistLocal() {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify({ mode: state.mode, manualTz: state.manualTz }))
  } catch { /* ignore */ }
}

const autoTimeZone = computed(() => state.ipTz || browserTimeZone() || 'America/Sao_Paulo')
const effectiveTimeZone = computed(() =>
  state.mode === 'manual' && isValidTimeZone(state.manualTz) ? state.manualTz : autoTimeZone.value
)

async function detectByIp(): Promise<string> {
  try {
    const resp = await apiFetch<{ timezone: string }>('/geo/timezone', undefined, false)
    if (resp?.timezone && isValidTimeZone(resp.timezone)) state.ipTz = resp.timezone
  } catch { /* mantém fallback do navegador */ }
  state.ipChecked = true
  return state.ipTz
}

// Carrega a preferência salva no servidor (chamado no boot autenticado)
async function initFromServer(): Promise<void> {
  if (state.serverLoaded) return
  state.serverLoaded = true
  try {
    const resp = await apiFetch<{ timezone: string }>('/me/timezone')
    if (resp && typeof resp.timezone === 'string') {
      if (resp.timezone && isValidTimeZone(resp.timezone)) {
        state.mode = 'manual'
        state.manualTz = resp.timezone
      } else {
        state.mode = 'auto'
        state.manualTz = ''
      }
      persistLocal()
    }
  } catch { /* mantém preferência local */ }
  if (!state.ipChecked) detectByIp()
}

async function setManual(tz: string): Promise<void> {
  if (!isValidTimeZone(tz)) return
  state.mode = 'manual'
  state.manualTz = tz
  persistLocal()
  await apiFetch('/me/timezone', { method: 'POST', body: JSON.stringify({ timezone: tz }) }).catch(() => { /* offline: fica local */ })
}

async function setAuto(): Promise<void> {
  state.mode = 'auto'
  state.manualTz = ''
  persistLocal()
  if (!state.ipChecked) detectByIp()
  await apiFetch('/me/timezone', { method: 'POST', body: JSON.stringify({ timezone: '' }) }).catch(() => { /* offline: fica local */ })
}

export function listTimeZones(): string[] {
  try {
    const intlWithValues = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    const zones = intlWithValues.supportedValuesOf?.('timeZone')
    if (zones && zones.length) return zones
  } catch { /* fallback abaixo */ }
  return [
    'America/Sao_Paulo', 'America/Manaus', 'America/Cuiaba', 'America/Fortaleza', 'America/Recife',
    'America/Belem', 'America/Rio_Branco', 'America/Noronha',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
    'America/Mexico_City', 'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Argentina/Buenos_Aires',
    'Atlantic/Azores', 'Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'Europe/Paris', 'Europe/Berlin',
    'Europe/Rome', 'Europe/Moscow', 'Africa/Lagos', 'Africa/Johannesburg',
    'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Singapore',
    'Australia/Sydney', 'Pacific/Auckland', 'UTC'
  ]
}

// ── Formatadores (sempre no fuso efetivo do usuário) ──

function toDate(value: string | Date): Date | null {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  const raw = `${value || ''}`.trim()
  if (!raw) return null
  const d = new Date(raw)
  return isNaN(d.getTime()) ? null : d
}

/** "16/07/2026 14:35" no fuso do usuário */
export function formatDateTimeTz(value: string | Date): string {
  const d = toDate(value)
  if (!d) return `${value || ''}`
  try {
    return d.toLocaleString('pt-BR', {
      timeZone: effectiveTimeZone.value,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).replace(',', '')
  } catch {
    return d.toLocaleString('pt-BR')
  }
}

/** "14:35" no fuso do usuário */
export function formatTimeTz(value: string | Date): string {
  const d = toDate(value)
  if (!d) return ''
  try {
    return d.toLocaleTimeString('pt-BR', {
      timeZone: effectiveTimeZone.value,
      hour: '2-digit', minute: '2-digit', hour12: false
    })
  } catch {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }
}

/** "16/07/2026" no fuso do usuário */
export function formatDateTz(value: string | Date): string {
  const d = toDate(value)
  if (!d) return `${value || ''}`
  try {
    return d.toLocaleDateString('pt-BR', { timeZone: effectiveTimeZone.value })
  } catch {
    return d.toLocaleDateString('pt-BR')
  }
}

/** Hora atual formatada num fuso arbitrário (para pré-visualizações) */
export function currentTimeInZone(tz: string): string {
  try {
    return new Date().toLocaleTimeString('pt-BR', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return ''
  }
}

/** Offset legível de um fuso, ex.: "UTC-03:00" */
export function zoneOffsetLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(new Date())
    const name = parts.find(p => p.type === 'timeZoneName')?.value || ''
    return name.replace('GMT', 'UTC')
  } catch {
    return ''
  }
}

export const timezoneStore = {
  state,
  autoTimeZone,
  effectiveTimeZone,
  detectByIp,
  initFromServer,
  setManual,
  setAuto
}
